import React, { useCallback, useEffect } from 'react';
import { ArrowLeft, X } from '@phosphor-icons/react';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { cn } from 'src/utils/className';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  showClose?: boolean;
  closeButtonMessage?: string;
  showBack?: boolean;
  onBack?: () => void;
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  children: React.ReactNode;
  actionButton?: React.ReactNode;
  footer?: React.ReactNode;
  showModalBorder?: boolean;
  className?: string;
};

export const Modal = ({
  isOpen,
  onClose,
  showClose = true,
  closeButtonMessage,
  showBack = false,
  onBack,
  title,
  subtitle,
  children,
  actionButton,
  footer,
  showModalBorder = false,
  className,
}: ModalProps) => {
  const handleClose = useCallback(
    (evt: React.MouseEvent | KeyboardEvent) => {
      if (!showClose) return;
      evt.stopPropagation();
      if (onClose) onClose();
    },
    [showClose, onClose],
  );

  const handleOverlayClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (!showClose) return;
    e.stopPropagation();
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) {
      handleClose(e);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-active');
    } else {
      document.body.classList.remove('modal-active');
    }

    return () => document.body.classList.remove('modal-active');
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if ((e.key === 'Escape' || e.key === 'Esc') && isOpen) {
        handleClose(e);
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, handleClose]);

  return (
    <div
      className={cn(
        `modal fixed w-full h-full top-0 left-0 flex items-center justify-center z-50`,
        isOpen
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none',
      )}
      onClick={handleOverlayClick}
    >
      <div className="modal-overlay absolute w-full h-full bg-neutral-700 opacity-50"></div>

      <div
        className={cn(
          'modal-container bg-neutral-0 w-11/12 mx-auto rounded shadow-lg z-50 flex flex-col lg:max-w-[60%] max-h-[96vh]',
          className,
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'flex justify-between items-center p-6 pb-4 flex-shrink-0',
            showModalBorder && 'border-b border-neutral-200',
          )}
        >
          <div className="flex items-center gap-4">
            {showBack && onBack && (
              <IconButton onClick={onBack} aria-label="Go back">
                <ArrowLeft size={24} />
              </IconButton>
            )}
            <div>
              <h2 className="text-h2 text-primary-h2">{title}</h2>
              {subtitle && (
                <p className="text-description text-neutral-400 mt-1">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {showClose && (
            <IconButton onClick={handleClose} aria-label="Close modal">
              <X size={24} />
            </IconButton>
          )}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6">{children}</div>

        {/* Footer */}
        {footer ||
          ((actionButton || showClose) && (
            <div
              className={cn(
                'flex justify-end gap-2 pt-4 px-6 pb-6 flex-shrink-0',
                showModalBorder && 'border-t border-neutral-200',
              )}
            >
              {showClose && (
                <Button onClick={handleClose} variant="outlined">
                  {closeButtonMessage || 'Close'}
                </Button>
              )}
              {actionButton}
            </div>
          ))}
      </div>
    </div>
  );
};
